import React, { useEffect, useState } from "react";
import { Button, Card, Modal, Text } from "react-native-paper";
import {
  FlatList,
  Image,
  StyleSheet,
  TouchableOpacity,
  View,
} from "react-native";
import { Feather } from "@expo/vector-icons";
import { useDispatch, useSelector } from "react-redux";
import { logout as logoutUser } from "../../../store/slices/user-slice";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { RootState } from "../../../store";
import { useNavigation, useRoute } from "@react-navigation/native";
import { Skeleton , Button as AntButton } from "antd";
import NotificationsModal from "./modals/NotificationsModal";
import * as SignalR from '@microsoft/signalr';
import { startNotificationHub } from "../../../store/services/notifications-signalr";


export interface WebPageHeaderProps {
  previousTab?: RouteTab;
  tabs?: RouteTab[];
}

type RouteTab = {
  title: string;
  routeName?: string;
  params?: any;
  disabled?: boolean;
  hidden?: boolean;
  replaceStack?: boolean;
};

interface PageHeaderProps {
  header: WebPageHeaderProps;
}

export default function PageHeader({ header }: PageHeaderProps) {
  const { user } = useSelector((state: RootState) => state.user);
  const navigation = useNavigation<any>();
  const route = useRoute();
  const { previousTab } = header;
  const dispatch = useDispatch();

  const [notificationsVisible, setNotificationsVisible] = useState(false);
  const unreadCount = useSelector((state: RootState) => state.notifications.unreadCount);

useEffect(() => {
  if (user?.id) {
    startNotificationHub(user.id);
  }
}, [user?.id]);


  const logout = async () => {
    dispatch(logoutUser());
    // try {
    //   await AsyncStorage.clear();
    // } catch (error) {
    //   console.log("Error clearing AsyncStorage:", error);
    // }
    navigation.replace("login");
  };

  const renderTab = (tab: RouteTab) => {
    const isCurrentTab = tab.routeName === route.name;
    const handleNavigation = () => {
      if (tab?.replaceStack) {
        navigation.replace(tab.routeName, tab?.params ?? {});
        return;
      }
      navigation.navigate(tab.routeName, tab?.params ?? {});
    };

    return (
      <TouchableOpacity
        key={tab.title}
        style={styles.tab}
        onPress={handleNavigation}
        disabled={isCurrentTab || tab.disabled}
      >
        <Text style={styles.tabText}>{tab.title}</Text>
      </TouchableOpacity>
    );
  };

  const renderPreviousTab = (tab: RouteTab) => {
    const handleNavigation = () => {
      if (tab?.replaceStack) {
        navigation.replace(tab.routeName, tab?.params ?? {});
        return;
      }
      navigation.navigate(tab.routeName, tab?.params ?? {});
    };

    return (
      <TouchableOpacity style={styles.tab} onPress={handleNavigation}>
        <Feather name="arrow-left" size={24} />
        <Text style={styles.tabText}>{previousTab.title}</Text>
      </TouchableOpacity>
    );
  };



  return (
    <Skeleton loading={!header}>
      <Card style={styles.headerContainer}>
        <Card.Content style={styles.tabsContainer}>
          {previousTab && renderPreviousTab(previousTab)}

          <FlatList
            ItemSeparatorComponent={() => (
              <View
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <View
                  style={{ width: 1, backgroundColor: "#9F9F9F", height: 32 }}
                />
              </View>
            )}
            horizontal
            data={(header?.tabs ?? []).filter((tab) => !tab?.hidden)}
            renderItem={({ item }) => renderTab(item)}
            keyExtractor={(item) => item.title}
          />

          {user?.role === "BuildingLead" && (
            <>
              <View style={styles.bellContainer}>
                <TouchableOpacity
                  style={styles.bellButton}
                  onPress={() => setNotificationsVisible(true)}
                >
                  <Feather name="bell" size={24} color="#1a4d78" />
                  {unreadCount > 0 && (
                    <View style={styles.badge}>
                      <Text style={styles.badgeText}>{unreadCount}</Text>
                    </View>
                  )}
                </TouchableOpacity>
              </View>

              
            </>
          )}

          <Image
            style={styles.pageHeaderLogo}
            source={require("../../../assets/images/fap-logo.png")}
            resizeMode="contain"
          />

          <View>
            <Button icon="account" mode="elevated" onPress={logout}>
              {user?.name}
            </Button>
          </View>
        </Card.Content>
      </Card>


      <NotificationsModal
          userId={user?.id || ""}
          visible={notificationsVisible}  
          onClose={() => setNotificationsVisible(false)}
          
        />
      
    </Skeleton>
  );
}

const styles = StyleSheet.create({
  headerContainer: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    paddingHorizontal: 8,
    marginVertical: 12,
    backgroundColor: "#fff",
  },
  tabsContainer: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
  },
  tab: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
    gap: 8,
    paddingHorizontal: 18,
    paddingVertical: 8,
  },
  tabText: {
    fontSize: 16,
    fontWeight: "600",
  },
  pageHeaderLogo: {
    width: 96,
    height: 48,
    marginRight: 24,
  },
  bellContainer: {
    position: "relative",
    marginHorizontal: 10,
  },
  bellButton: {
    position: "relative",
    padding: 10,
  },
  badge: {
    position: "absolute",
    top: 4,
    right: 4,
    backgroundColor: "red",
    borderRadius: 10,
    minWidth: 18,
    height: 18,
    justifyContent: "center",
    alignItems: "center",
    paddingHorizontal: 4,
  },
  badgeText: {
    color: "white",
    fontSize: 11,
    fontWeight: "bold",
  },
  modalOverlay: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: "rgba(0,0,0,0.5)",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 9999,
    elevation: 9999,
  },
  modalContainer: {
    width: "85%",
    backgroundColor: "white",
    padding: 20,
    borderRadius: 12,
    maxHeight: "70%",
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: "bold",
    marginBottom: 12,
  },
  notificationItem: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: "#eee",
  },
  notificationText: {
    marginLeft: 8,
    fontSize: 15,
  },
});
